import axios, { AxiosRequestConfig } from "axios";
import { API_CONSTANT_ONBOARDING, PORTAL_ORIGIN } from "utils";

export type OnboardingSession = {
  INSTITUTE_ID?: number | string;
  BRANCH_ID?: number | string;
  ACADEMIC_YEAR_ID?: number | string;
  DATABASE_NAME?: string;
  ONBOARDING_ACCESS_TOKEN?: string;
  ACCESS_TOKEN?: string;
  access_token?: string;
  token?: string;
  AUTHORIZATION?: string;
  Authorization?: string;
  authorization?: string;
  LOGIN_REQUIRED?: boolean;
  [key: string]: unknown;
};

export const getOnboardingSession = (): OnboardingSession => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem("_DATA_") || "{}");
    // DevTools/manual saves are sometimes JSON-stringified twice.
    return typeof parsed === "string" ? JSON.parse(parsed) : parsed;
  } catch {
    return {};
  }
};

export const getOnboardingHeaders = (
  session: OnboardingSession = getOnboardingSession(),
) => {
  const tokenValue =
    session.ONBOARDING_ACCESS_TOKEN ||
    session.ACCESS_TOKEN ||
    session.access_token ||
    session.token ||
    session.AUTHORIZATION ||
    session.Authorization ||
    session.authorization ||
    sessionStorage.getItem("access_token") ||
    sessionStorage.getItem("token");
  const token =
    typeof tokenValue === "string"
      ? tokenValue
          .trim()
          .replace(/^["']|["']$/g, "")
          .replace(/^Bearer\s+/i, "")
      : "";

  return {
    ...(token && !["undefined", "null"].includes(token.toLowerCase())
      ? { Authorization: `Bearer ${token}` }
      : {}),
    ...(session.INSTITUTE_ID
      ? { "institute-id": String(session.INSTITUTE_ID) }
      : {}),
    ...(session.BRANCH_ID ? { "branch-id": String(session.BRANCH_ID) } : {}),
    ...(session.DATABASE_NAME ? { database: session.DATABASE_NAME } : {}),
  };
};

export const onboardingRequestConfig = (
  config: AxiosRequestConfig = {},
  session: OnboardingSession = getOnboardingSession(),
): AxiosRequestConfig => ({
  ...config,
  headers: {
    ...getOnboardingHeaders(session),
    ...config.headers,
  },
});

export const onboardingUploadRequestConfig = (
  config: AxiosRequestConfig = {},
  session: OnboardingSession = getOnboardingSession(),
): AxiosRequestConfig => ({
  ...config,
  headers: {
    ...getOnboardingHeaders(session),
    // Discussed with Sanjay: use "onboarding" for now; this must be changed in the future.
    database: session.DATABASE_NAME || "onboarding",
    ...config.headers,
  },
});

/**
 * Drop the onboarding token from the stored session.
 *
 * Once the session has been exchanged the server has revoked it, so keeping the
 * token around only guarantees that anything else touching it fails with a
 * confusing "session expired" message.
 */
const clearOnboardingToken = () => {
  try {
    const session = getOnboardingSession();
    delete session.ONBOARDING_ACCESS_TOKEN;
    sessionStorage.setItem("_DATA_", JSON.stringify(session));
  } catch {
    /* the session is already unreadable; nothing left to clear */
  }
};

export type FinalizeResult =
  | { status: "signed-in"; session: Record<string, unknown> }
  | { status: "manual-login"; reason: string };

// API_CONSTANT_ONBOARDING carries a trailing slash in some environments and not
// in others, so join rather than interpolate.
const apiUrl = (path: string) =>
  `${API_CONSTANT_ONBOARDING.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;

/**
 * Finish onboarding by trading the onboarding session for a signed-in one.
 * The branch stays PENDING, so the portal takes it through initial setup.
 *
 * Never throws. By the time this runs the institute's data is already imported,
 * so a failure here means "you'll have to sign in yourself", not "onboarding
 * failed" — and it must not be presented as the latter.
 */
export const finalizeOnboarding = async (): Promise<FinalizeResult> => {
  const session = getOnboardingSession();

  try {
    const response = await axios.post(
      apiUrl("auth/onboarding/session/exchange"),
      { DEVICE_TYPE: "WEB" },
      onboardingRequestConfig({}, session),
    );

    const login = response?.data?.data;
    if (response?.data?.code !== 200 || !login?.ACCESS_TOKEN) {
      return {
        status: "manual-login",
        reason: response?.data?.message || "Could not sign you in automatically.",
      };
    }

    clearOnboardingToken();
    return { status: "signed-in", session: login };
  } catch (error) {
    console.error("Could not exchange the onboarding session", error);
    return {
      status: "manual-login",
      reason: "Could not sign you in automatically.",
    };
  }
};

/**
 * Hand the freshly minted session to the portal, which lives on the branch's
 * own domain. Cross-origin storage is not writable from here, so the session
 * travels in the URL fragment: fragments are not sent to servers and so do not
 * end up in access logs. The portal consumes it and clears it immediately.
 */
export const redirectToPortal = (login: Record<string, unknown>) => {
  if (!PORTAL_ORIGIN) return false;

  const payload = encodeURIComponent(btoa(JSON.stringify(login)));
  window.location.href = `${PORTAL_ORIGIN.replace(/\/+$/, "")}/session-handoff#${payload}`;
  return true;
};

export const requireProtectedOnboardingSession = () => {
  const session = getOnboardingSession();
  const headers = getOnboardingHeaders(session);
  if (!headers.Authorization) {
    throw new Error(
      session.LOGIN_REQUIRED
        ? "Founder login is required before onboarding can continue."
        : "Your onboarding session is missing or expired. Please verify again.",
    );
  }
  if (!headers["institute-id"]) {
    throw new Error("Institute ID is missing from the onboarding session.");
  }
  if (!headers["branch-id"]) {
    throw new Error("Branch ID is missing from the onboarding session.");
  }
  if (!headers.database) {
    throw new Error("Database name is missing from the onboarding session.");
  }
  return session;
};
